import type { KeyboardEvent } from 'react';
import { IconButton, Input } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { Cancel01Icon, Search01Icon } from '@terra/ui-icons';

const MENU_DISMISS_KEY = 'Escape';

type TargetSelectorSearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clearLabel?: string;
};

export function TargetSelectorSearchInput({
  value,
  onChange,
  placeholder,
  clearLabel,
}: TargetSelectorSearchInputProps) {
  const showClear = Boolean(clearLabel) && value.length > 0;

  return (
    <Box position="relative">
      <Box
        position="absolute"
        left="2.5"
        top="50%"
        transform="translateY(-50%)"
        zIndex={1}
        pointerEvents="none"
        color="fg.neutral.muted"
        display="flex"
        alignItems="center"
      >
        <Search01Icon size={14} />
      </Box>
      <Input
        size="sm"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key !== MENU_DISMISS_KEY) {
            event.stopPropagation();
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        pl="8"
        pr={showClear ? '8' : undefined}
        borderWidth="1px"
        borderColor="border.neutral.subtle"
        borderStyle="solid"
        outline="none"
        _hover={{
          borderColor: 'border.neutral.subtle',
        }}
        _focus={{
          outline: 'none',
          borderColor: 'border.neutral.subtle',
          boxShadow: 'none',
        }}
        _focusVisible={{
          outline: 'none',
          borderColor: 'border.neutral.subtle',
          boxShadow: 'none',
        }}
      />
      {showClear && (
        <Box
          position="absolute"
          right="1"
          top="50%"
          transform="translateY(-50%)"
          zIndex={1}
          display="flex"
          alignItems="center"
        >
          <IconButton
            variant="ghost"
            size="2xs"
            aria-label={clearLabel}
            onClick={() => onChange('')}
          >
            <Cancel01Icon size={12} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
